import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './auth.css';
import { useAuth } from './AuthContext';

// Keep in sync with auth-construct.ts → UserPool.passwordPolicy. Cognito
// also enforces these server-side; the client check is just for a friendlier
// error than a server round-trip.
const PASSWORD_MIN = 10;
const passwordOk = (p: string) =>
  p.length >= PASSWORD_MIN && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

const SignUp: React.FC = () => {
  const { signUp, showSignIn, showLanding, federatedSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!passwordOk(password)) {
      setError(`Password must be ${PASSWORD_MIN}+ chars with upper, lower, and a digit.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Join StudyBuddy</h1>
        <p className="subtitle">We'll send a verification code to your email.</p>
        {/* Consent line — placed above both sign-up methods so it applies to
            email-and-password AND Continue-with-Google. GDPR/CCPA expect an
            explicit "by signing up you agree" moment that's visible at the
            point of account creation, not just buried in the footer. */}
        <p className="auth-consent">
          By creating an account, you agree to our{' '}
          <Link to="/terms">Terms of Service</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
        <button
          className="primary"
          type="button"
          style={{ width: '100%', marginBottom: 4 }}
          onClick={() => federatedSignIn('Google')}
        >
          Continue with Google
        </button>
        <div className="auth-divider"><span>or</span></div>
        <form onSubmit={submit}>
          <label htmlFor="email">Email</label>
          <input
            id="email" type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <label htmlFor="password">Password</label>
          <input
            id="password" type="password" required autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <label htmlFor="confirm">Confirm password</label>
          <input
            id="confirm" type="password" required autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
          <button className="primary" type="submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        {error && <div className="auth-message error">{error}</div>}
        <div className="auth-footer">
          Already have an account?{' '}
          <button className="link" type="button" onClick={showSignIn}>
            Sign in
          </button>
        </div>
        <div className="auth-footer" style={{ marginTop: 6 }}>
          <button className="link" type="button" onClick={showLanding}>
            ← back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
